import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Palette,
  Type as TypeIcon,
  Sparkles,
  Bell,
  Layers,
  MousePointerClick,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  X,
  Copy as CopyIcon,
  LayoutGrid,
  Search,
  Filter,
  Plus,
  Pencil,
  Trash2,
  Euro,
  FileText,
  Users as UsersIcon,
  Clock,
  Printer,
  Code2,
  Save,
  Download,
  Upload,
  RefreshCw,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Star,
  Settings,
  User,
  LogOut,
  Bookmark,
  Tag,
  ChevronRight,
  Wand2,
  ClipboardCheck,
  ShieldCheck,
} from "lucide-react";
import { buildPrintDocument, openPrintWindow } from "@/utils/printLayout";
import { APP_RELEASE } from "@/config/appRelease";
import PrintFooter from "@/components/common/PrintFooter";
import { KpiPill } from "@/components/common/ModulePageLayout";

/**
 * DesignSystemPage — v1.6.0 · 22/07/2026.
 *
 * Storybook interno accessibile solo agli admin (guarded upstream in App.js).
 * Documenta live la palette, i font, le utility class, i pattern di
 * bottoni/dialoghi/toast/tab/alert del design system Gruppo Impresa Suite.
 *
 * NON è un tab operativo: è una vetrina statica dei componenti brand
 * per operatori, collaboratori esterni e reviewer del codice.
 *
 * v1.6.0 — Aggiunte sezioni "Iconografia standard" e blueprint
 * "Wizard multi-step". Footer istituzionale allineato al nuovo
 * <PrintFooter /> unificato (rimossa dicitura "Revisione" e status).
 */

const COLOR_TOKENS = [
  { name: "--gis-green",       hex: "#08684F", use: "Colore primario · bottoni azione · header dialog · tab attivo" },
  { name: "--gis-green-hover", hex: "#065A44", use: "Hover primario" },
  { name: "--gis-green-dark",  hex: "#044A38", use: "Testo contrast su chiaro · gradient H1" },
  { name: "--gis-green-50",    hex: "#E6F6EE", use: "Sfondo login · superfici tenui · hover neutri" },
  { name: "--gis-green-100",   hex: "#C7EBD8", use: "Badge · chip hover" },
  { name: "--gis-green-200",   hex: "#97D9B8", use: "Bordi tenui · divider brand" },
  { name: "--gis-green-500",   hex: "#22A87C", use: "Success accent (alternativa a emerald-500)" },
  { name: "--gis-gold",        hex: "#F2C94C", use: "Accento oro · icone brand · highlight positivi" },
  { name: "--gis-gold-hover",  hex: "#E4B733", use: "Hover oro" },
  { name: "--gis-gold-dark",   hex: "#B8901F", use: "Testo oro su chiaro (WCAG AA)" },
  { name: "--gis-gold-50",     hex: "#FEF7DC", use: "Sfondi tenui · banner warning brand" },
  { name: "--gis-red",         hex: "#E74C3C", use: "Pallino sicurezza · alert brand" },
  { name: "--gis-red-hover",   hex: "#C0392B", use: "Hover rosso brand" },
  { name: "--gis-gray",        hex: "#95A5A6", use: "Neutro brand · elementi decorativi" },
  { name: "--gis-gray-dark",   hex: "#6C7A7D", use: "Testo secondario alternativo" },
];

const FONT_SPECS = [
  { name: "Wordmark logo",  size: "text-xl",  weight: 700, family: "Montserrat" },
  { name: "H1 pagina",      size: "text-4xl", weight: 700, family: "Montserrat" },
  { name: "H2 sezione",     size: "text-2xl", weight: 600, family: "Montserrat" },
  { name: "Titolo dialogo", size: "text-xl",  weight: 600, family: "Montserrat" },
  { name: "Bottone azione", size: "text-sm",  weight: 600, family: "Montserrat" },
  { name: "Label KPI",      size: "text-[11px]", weight: 600, family: "Montserrat" },
  { name: "Corpo",          size: "text-base", weight: 400, family: "Inter" },
  { name: "Metadati",       size: "text-xs",  weight: 500, family: "Inter" },
];

const UTILITY_CLASSES = [
  { cls: ".gis-heading",       desc: "Font Montserrat (titoli e label)" },
  { cls: ".gis-heading-wide",  desc: "Montserrat UPPERCASE tracking largo" },
  { cls: ".gis-body",          desc: "Font Inter (corpo/metadati)" },
  { cls: ".gis-btn-label",     desc: "Bottone: Montserrat semibold tracking" },
  { cls: ".gis-btn-primary",   desc: "Bottone azione principale (bg verde brand)" },
  { cls: ".gis-text-green",    desc: "color: var(--gis-green)" },
  { cls: ".gis-text-gold",     desc: "color: var(--gis-gold-dark)" },
  { cls: ".gis-bg-green",      desc: "background: var(--gis-green)" },
  { cls: ".gis-bg-mint",       desc: "background: var(--gis-green-50)" },
  { cls: ".gis-border-green",  desc: "border-color: var(--gis-green)" },
  { cls: ".gis-focus-ring",    desc: "Focus ring verde su :focus-visible" },
  { cls: ".gis-tab-trigger",   desc: "Tab attivo con verde brand (opt-in su TabsTrigger)" },
];

/**
 * ICON_LIBRARY — Iconografia standard Gruppo Impresa Suite (v1.6.0).
 * Mappa semantica delle icone lucide-react da usare in modo coerente
 * nell'intera app. NON introdurre nuove icone senza aggiungerle qui.
 *
 * Regole:
 *   - Un solo glifo per significato (evita duplicati semantici).
 *   - Dimensione standard: h-4 w-4 (bottoni), h-3.5 w-3.5 (tabelle),
 *     h-5 w-5 (banner/alert), h-6 w-6 (empty state).
 *   - Colore = brand verde per azioni principali · rosso per delete
 *     · oro per warning · slate-500 per neutrali.
 */
const ICON_LIBRARY = [
  { icon: Info,           name: "Info",          semantic: "info",     use: "Banner informativo, tooltip, help contestuale" },
  { icon: CheckCircle2,   name: "CheckCircle2",  semantic: "success",  use: "Successo salvataggio, step completato" },
  { icon: AlertTriangle,  name: "AlertTriangle", semantic: "warning",  use: "Attenzione, conferma azione delicata" },
  { icon: XCircle,        name: "XCircle",       semantic: "error",    use: "Errore, validazione fallita" },
  { icon: Plus,           name: "Plus",          semantic: "new",      use: "Creazione nuovo elemento (cliente/pratica/fattura)" },
  { icon: Pencil,         name: "Pencil",        semantic: "edit",     use: "Modifica riga/scheda esistente" },
  { icon: Trash2,         name: "Trash2",        semantic: "delete",   use: "Elimina (sempre in rosso · richiedi conferma)" },
  { icon: Save,           name: "Save",          semantic: "save",     use: "Salva form/pratica/preventivo" },
  { icon: Search,         name: "Search",        semantic: "search",   use: "Ricerca full-text · sempre a sinistra dell'input" },
  { icon: Filter,         name: "Filter",        semantic: "filter",   use: "Apri filtri avanzati" },
  { icon: Download,       name: "Download",      semantic: "download", use: "Scarica file (PDF/Excel)" },
  { icon: Upload,         name: "Upload",        semantic: "upload",   use: "Carica documento" },
  { icon: Printer,        name: "Printer",       semantic: "print",    use: "Stampa (fascicolo, mandato, elenco)" },
  { icon: Mail,           name: "Mail",          semantic: "email",    use: "Invia email, template email" },
  { icon: Phone,          name: "Phone",         semantic: "phone",    use: "Contatti telefonici cliente" },
  { icon: Calendar,       name: "Calendar",      semantic: "date",     use: "Data/scadenza/agenda" },
  { icon: Clock,          name: "Clock",         semantic: "time",     use: "Ora/tempo/scadenze imminenti" },
  { icon: Euro,           name: "Euro",          semantic: "money",    use: "Importi, fatture, incassi" },
  { icon: FileText,       name: "FileText",      semantic: "document", use: "Documento generico, fascicolo" },
  { icon: UsersIcon,      name: "Users",         semantic: "people",   use: "Clienti, operatori, team" },
  { icon: User,           name: "User",          semantic: "person",   use: "Singolo utente/operatore" },
  { icon: MapPin,         name: "MapPin",        semantic: "address",  use: "Indirizzo, sede, geolocalizzazione" },
  { icon: Lock,           name: "Lock",          semantic: "locked",   use: "Contenuto protetto, permesso mancante" },
  { icon: Unlock,         name: "Unlock",        semantic: "unlocked", use: "Contenuto sbloccato dopo autenticazione" },
  { icon: Eye,            name: "Eye",           semantic: "view",     use: "Visualizza dettaglio, anteprima" },
  { icon: EyeOff,         name: "EyeOff",        semantic: "hide",     use: "Nascondi password/dettagli" },
  { icon: RefreshCw,      name: "RefreshCw",     semantic: "refresh",  use: "Ricarica lista, sincronizza" },
  { icon: Settings,       name: "Settings",      semantic: "settings", use: "Impostazioni, configurazione" },
  { icon: Bell,           name: "Bell",          semantic: "notify",   use: "Notifiche, promemoria" },
  { icon: Star,           name: "Star",          semantic: "favorite", use: "Preferito, cliente premium" },
  { icon: Bookmark,       name: "Bookmark",      semantic: "bookmark", use: "Segnalibro, salvataggio quick access" },
  { icon: Tag,            name: "Tag",           semantic: "tag",      use: "Etichetta, categoria pratica" },
  { icon: ShieldCheck,    name: "ShieldCheck",   semantic: "verified", use: "Verificato, validazione CF/Codice Fiscale" },
  { icon: ClipboardCheck, name: "ClipboardCheck", semantic: "checklist", use: "Checklist, task completato" },
  { icon: Wand2,          name: "Wand2",         semantic: "wizard",   use: "Wizard multi-step, procedura guidata" },
  { icon: LogOut,         name: "LogOut",        semantic: "logout",   use: "Uscita, chiusura sessione" },
];

function ColorSwatch({ token }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`var(${token.name})`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="group flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 hover:border-[var(--gis-green)] hover:shadow-md transition-all text-left"
      data-testid={`ds-color-${token.name.replace(/^--/, "")}`}
      title={`Clicca per copiare ${token.name}`}
    >
      <div
        className="h-12 w-12 rounded-lg border border-slate-100 shrink-0"
        style={{ backgroundColor: token.hex }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <code className="gis-body text-xs font-semibold text-slate-800">{token.name}</code>
          {copied ? (
            <CheckCircle2 className="h-3 w-3 text-[var(--gis-green)]" />
          ) : (
            <CopyIcon className="h-3 w-3 text-slate-300 group-hover:text-[var(--gis-green)]" />
          )}
        </div>
        <div className="text-[10px] font-mono text-slate-400">{token.hex}</div>
        <div className="text-[11px] text-slate-600 gis-body line-clamp-1">{token.use}</div>
      </div>
    </button>
  );
}

function IconTile({ entry }) {
  const [copied, setCopied] = useState(false);
  const Icon = entry.icon;
  const toneColor =
    entry.semantic === "error" || entry.semantic === "delete" ? "#dc2626" :
    entry.semantic === "warning" ? "var(--gis-gold-dark)" :
    entry.semantic === "success" || entry.semantic === "verified" ? "var(--gis-green)" :
    "var(--gis-green-dark)";
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(`import { ${entry.name} } from "lucide-react";`);
      setCopied(true);
      toast.success(`Import di ${entry.name} copiato`);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error("Copia non disponibile nel browser");
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      title={entry.use}
      className="group flex items-start gap-2.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2 hover:border-[var(--gis-green)] hover:shadow-sm transition-all text-left"
      data-testid={`ds-icon-${entry.name}`}
    >
      <span
        className="h-8 w-8 shrink-0 rounded-md flex items-center justify-center"
        style={{ backgroundColor: "var(--gis-green-50)" }}
      >
        <Icon className="h-4 w-4" style={{ color: toneColor }} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1">
          <code className="gis-body text-[11px] font-semibold text-slate-800 truncate">{entry.name}</code>
          {copied ? (
            <CheckCircle2 className="h-3 w-3 text-[var(--gis-green)] shrink-0" />
          ) : (
            <CopyIcon className="h-3 w-3 text-slate-300 group-hover:text-[var(--gis-green)] shrink-0" />
          )}
        </span>
        <span className="block text-[9.5px] uppercase tracking-wider text-slate-400 gis-heading">{entry.semantic}</span>
        <span className="block text-[11px] text-slate-600 gis-body line-clamp-2 leading-tight mt-0.5">{entry.use}</span>
      </span>
    </button>
  );
}

function Section({ id, icon: Icon, title, subtitle, children }) {
  return (
    <section id={id} className="scroll-mt-16">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-5 w-5" style={{ color: "var(--gis-green)" }} />
        <h2 className="gis-heading text-xl font-semibold text-slate-800">{title}</h2>
      </div>
      {subtitle && <p className="text-sm text-slate-500 gis-body mb-4">{subtitle}</p>}
      {children}
    </section>
  );
}

/**
 * CopySnippetButton — pulsante che copia il markup JSX di un pattern
 * negli appunti. Il caller fornisce il testo tramite prop `snippet`.
 * Mostra feedback "Copiato!" per 1.2s.
 */
function CopySnippetButton({ snippet, testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Snippet JSX copiato negli appunti");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copia non disponibile nel browser");
    }
  };
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onCopy}
      className="gis-border-green gis-text-green hover:bg-[var(--gis-green-50)]"
      data-testid={testId}
    >
      {copied ? (
        <><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Copiato!</>
      ) : (
        <><Code2 className="mr-1.5 h-3.5 w-3.5" /> Copia snippet JSX</>
      )}
    </Button>
  );
}

/** Snippet JSX pronti da copiare per ogni pattern (v1.5.0). */
const SNIPPETS = {
  moduleLayout: `import ModulePageLayout, { KpiPill } from "@/components/common/ModulePageLayout";
import { FolderOpen, Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export function RegistroFattureTab() {
  return (
    <ModulePageLayout
      testid="fatture-module"
      icon={FolderOpen}
      title="Registro Fatture"
      subtitle="Elenco fatture emesse dello studio"
      primaryAction={{
        label: "Nuova Fattura",
        icon: Plus,
        onClick: () => openNewInvoiceDialog(),
        testid: "new-invoice-btn",
      }}
      kpiStrip={
        <>
          <KpiPill value={124} label="Totali" tone="brand" />
          <KpiPill value={12}  label="In Bozza" tone="warning" />
          <KpiPill value={98}  label="Inviate" tone="success" />
          <KpiPill value={8}   label="Da Incassare" tone="danger" />
          <KpiPill value={3}   label="Oggi" tone="info" />
        </>
      }
      toolbar={
        <>
          <div className="relative flex-1 min-w-[220px] max-w-[420px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Cerca..." className="pl-8 h-9 text-sm" />
          </div>
          {/* … SelectTrigger cliente, stato, periodo … */}
          <Button variant="outline" size="sm" className="h-9 text-xs">Reset</Button>
        </>
      }
    >
      {/* Area operativa: la tabella riceve ~80% dell'altezza */}
      <YourTable />
    </ModulePageLayout>
  );
}
// Altezze standard (in index.css):
//   --gis-mod-header-h:  80px
//   --gis-mod-kpi-h:     60px
//   --gis-mod-toolbar-h: 56px
`,

  kpiGrid: `<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
  {kpiList.map((k) => (
    <div key={k.label} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow"
         style={{ borderColor: "var(--gis-green-200)" }}>
      <div className="flex items-center justify-between mb-2">
        <p className="gis-heading text-[10px] uppercase tracking-wider text-slate-500">{k.label}</p>
        <k.icon className="h-4 w-4" style={{ color: "var(--gis-green)" }} />
      </div>
      <p className="gis-heading text-2xl font-bold text-slate-900 tabular-nums">{k.value}</p>
      <p className="text-xs text-slate-500 gis-body">{k.sub}</p>
    </div>
  ))}
</div>`,

  form2col: `<div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
  <div className="h-1.5 w-full" style={{ backgroundColor: "var(--gis-green)" }} />
  <div className="p-5 space-y-4">
    <h3 className="gis-heading text-lg font-semibold" style={{ color: "var(--gis-green)" }}>
      Nuovo Cliente
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-1">
        <Label className="gis-body text-sm text-slate-700 font-medium">Cognome</Label>
        <Input className="focus:border-[var(--gis-green)]" />
      </div>
      {/* … altri campi affiancati … */}
    </div>
    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
      <Button variant="outline">Annulla</Button>
      <Button className="gis-btn-primary">Salva</Button>
    </div>
  </div>
</div>`,

  tableToolbar: `<div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
  <div className="px-4 py-3 border-b bg-[var(--gis-green-50)]/60 flex items-center gap-2">
    <div className="relative flex-1 max-w-sm">
      <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
      <Input placeholder="Cerca…" className="pl-8 h-9 focus:border-[var(--gis-green)]" />
    </div>
    <Button variant="outline" size="sm" className="gis-border-green gis-text-green">
      <Filter className="mr-1.5 h-3.5 w-3.5" /> Filtri
    </Button>
    <div className="flex-1" />
    <Button size="sm" className="gis-btn-primary">
      <Plus className="mr-1.5 h-3.5 w-3.5" /> Nuovo
    </Button>
  </div>
  <table className="w-full text-sm">
    <thead className="bg-slate-50">
      <tr><th className="gis-heading text-[11px] uppercase tracking-wider text-slate-600 text-left px-4 py-2">Colonna</th></tr>
    </thead>
    <tbody className="divide-y divide-slate-100">
      {rows.map((r) => (
        <tr key={r.id} className="hover:bg-[var(--gis-green-50)]/40">
          <td className="px-4 py-2.5 gis-body">{r.name}</td>
        </tr>
      ))}
    </tbody>
  </table>
</div>`,

  emptyState: `<div className="bg-white border border-dashed border-slate-300 rounded-lg py-12 px-6 flex flex-col items-center text-center">
  <div className="flex items-center gap-2 mb-4" aria-hidden="true">
    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: "var(--gis-red)" }} />
    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: "var(--gis-gray)" }} />
    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: "var(--gis-gold)" }} />
  </div>
  <h3 className="gis-heading text-lg font-semibold" style={{ color: "var(--gis-green)" }}>
    Nessun elemento
  </h3>
  <p className="text-sm text-slate-500 gis-body mt-1">Inizia creando il primo elemento.</p>
  <div className="mt-4 flex gap-2">
    <Button className="gis-btn-primary"><Plus className="mr-1.5 h-4 w-4" /> Crea il primo</Button>
    <Button variant="outline" className="gis-border-green gis-text-green">Importa da CSV</Button>
  </div>
</div>`,

  printA4: `import { buildPrintDocument, openPrintWindow } from "@/utils/printLayout";

// … costruisci il corpo (HTML tabelle/paragrafi) …
const bodyHtml = \`
  <h2>Sezione 1</h2>
  <table>
    <thead><tr><th>Colonna A</th><th>Colonna B</th></tr></thead>
    <tbody><tr><td>…</td><td>…</td></tr></tbody>
  </table>
\`;

const html = buildPrintDocument({
  title: "Titolo del documento",
  subtitle: "Dettagli opzionali",
  bodyHtml,
  orientation: "landscape",        // "portrait" | "landscape"
  studioName: "Studio Bonaiuti STP",
  studioLine: "Via ... · P.IVA ... · info@…",
});
openPrintWindow(html);
// Il footer istituzionale (Suite · Banca dati · versione · pagina X/Y)
// viene generato automaticamente su ogni pagina.`,

  wizard: `import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, ChevronRight, Wand2 } from "lucide-react";

const STEPS = [
  { key: "anagrafica", label: "Anagrafica" },
  { key: "dati",       label: "Dati pratica" },
  { key: "conferma",   label: "Conferma" },
];

export function WizardMultiStep({ onSubmit }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ nome: "", cognome: "", oggetto: "" });

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="h-1.5 w-full" style={{ backgroundColor: "var(--gis-green)" }} />
      {/* Stepper */}
      <div className="px-5 pt-4 flex items-center gap-2">
        {STEPS.map((s, i) => {
          const active = i === step;
          const done = i < step;
          return (
            <div key={s.key} className="flex items-center gap-2">
              <div
                className="h-7 w-7 rounded-full flex items-center justify-center gis-heading text-xs font-semibold"
                style={{
                  backgroundColor: done || active ? "var(--gis-green)" : "var(--gis-green-50)",
                  color: done || active ? "white" : "var(--gis-green)",
                  border: active ? "2px solid var(--gis-gold)" : "none",
                }}
              >
                {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <span
                className="gis-heading text-xs"
                style={{ color: active ? "var(--gis-green-dark)" : "#64748b", fontWeight: active ? 700 : 500 }}
              >
                {s.label}
              </span>
              {i < STEPS.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
            </div>
          );
        })}
      </div>

      {/* Body per step */}
      <div className="p-5 space-y-4 min-h-[180px]">
        {step === 0 && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Cognome</Label>
              <Input value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Nome</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-1">
            <Label>Oggetto</Label>
            <Input value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })} />
          </div>
        )}
        {step === 2 && (
          <div className="bg-[var(--gis-green-50)] border rounded p-3 text-sm gis-body">
            <b>Riepilogo:</b> {form.cognome} {form.nome} — {form.oggetto || "—"}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-between gap-2 px-5 py-3 border-t border-slate-100">
        <Button variant="outline" onClick={prev} disabled={step === 0}>Indietro</Button>
        {step < STEPS.length - 1 ? (
          <Button className="gis-btn-primary" onClick={next}>
            Avanti <ChevronRight className="ml-1.5 h-4 w-4" />
          </Button>
        ) : (
          <Button className="gis-btn-primary" onClick={() => onSubmit?.(form)}>
            <Wand2 className="mr-1.5 h-4 w-4" /> Conferma
          </Button>
        )}
      </div>
    </div>
  );
}`,
};

/**
 * WizardDemo — Demo interattiva del pattern Wizard multi-step (3 step:
 * Anagrafica → Dati → Conferma). Usata nello Storybook per mostrare
 * lo stepper con verde brand + accento oro sullo step attivo.
 */
function WizardDemo() {
  const STEPS = [
    { key: "anagrafica", label: "Anagrafica" },
    { key: "dati", label: "Dati pratica" },
    { key: "conferma", label: "Conferma" },
  ];
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ cognome: "", nome: "", oggetto: "" });
  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ds-wizard-demo">
      <div className="h-1.5 w-full" style={{ backgroundColor: "var(--gis-green)" }} />
      {/* Stepper */}
      <div className="px-5 pt-4 flex flex-wrap items-center gap-2">
        {STEPS.map((s, i) => {
          const active = i === step;
          const done = i < step;
          return (
            <div key={s.key} className="flex items-center gap-2" data-testid={`ds-wizard-step-${i}`}>
              <div
                className="h-7 w-7 rounded-full flex items-center justify-center gis-heading text-xs font-semibold"
                style={{
                  backgroundColor: done || active ? "var(--gis-green)" : "var(--gis-green-50)",
                  color: done || active ? "white" : "var(--gis-green)",
                  border: active ? "2px solid var(--gis-gold)" : "2px solid transparent",
                }}
              >
                {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <span
                className="gis-heading text-xs"
                style={{ color: active ? "var(--gis-green-dark)" : "#64748b", fontWeight: active ? 700 : 500 }}
              >
                {s.label}
              </span>
              {i < STEPS.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
            </div>
          );
        })}
      </div>
      {/* Body */}
      <div className="p-5 space-y-4 min-h-[180px]">
        {step === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="gis-body text-sm text-slate-700 font-medium">Cognome</Label>
              <Input
                value={form.cognome}
                onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                placeholder="es. Rossi"
                className="focus:border-[var(--gis-green)]"
                data-testid="ds-wizard-input-cognome"
              />
            </div>
            <div className="space-y-1">
              <Label className="gis-body text-sm text-slate-700 font-medium">Nome</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="es. Mario"
                className="focus:border-[var(--gis-green)]"
                data-testid="ds-wizard-input-nome"
              />
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-1">
            <Label className="gis-body text-sm text-slate-700 font-medium">Oggetto pratica</Label>
            <Input
              value={form.oggetto}
              onChange={(e) => setForm({ ...form, oggetto: e.target.value })}
              placeholder="Es. Successione ereditaria, apertura P.IVA…"
              className="focus:border-[var(--gis-green)]"
              data-testid="ds-wizard-input-oggetto"
            />
          </div>
        )}
        {step === 2 && (
          <div
            className="rounded-lg p-3 gis-body text-sm space-y-1"
            style={{ backgroundColor: "var(--gis-green-50)", border: "1px solid var(--gis-green-200)" }}
            data-testid="ds-wizard-summary"
          >
            <div><b>Cliente:</b> {form.cognome || "—"} {form.nome || ""}</div>
            <div><b>Oggetto:</b> {form.oggetto || "—"}</div>
            <div className="pt-1 text-xs text-slate-500">Premi <b>Conferma</b> per creare l&apos;entità.</div>
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="flex justify-between gap-2 px-5 py-3 border-t border-slate-100">
        <Button
          variant="outline"
          onClick={prev}
          disabled={step === 0}
          data-testid="ds-wizard-prev"
        >
          Indietro
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            className="gis-btn-primary"
            onClick={next}
            data-testid="ds-wizard-next"
          >
            Avanti <ChevronRight className="ml-1.5 h-4 w-4" />
          </Button>
        ) : (
          <Button
            className="gis-btn-primary"
            onClick={() => {
              toast.success(`Wizard completato · ${form.cognome} ${form.nome}`.trim());
              setStep(0);
              setForm({ cognome: "", nome: "", oggetto: "" });
            }}
            data-testid="ds-wizard-submit"
          >
            <Wand2 className="mr-1.5 h-4 w-4" /> Conferma
          </Button>
        )}
      </div>
    </div>
  );
}

export default function DesignSystemPage({ onClose }) {
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [demoTab, setDemoTab] = useState("uno");
  const [demoInput, setDemoInput] = useState("");

  return (
    <div className="p-4 sm:p-6 space-y-8 max-w-6xl mx-auto" data-testid="design-system-page">
      {/* Header pagina */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1
            className="gis-heading text-3xl sm:text-4xl font-bold"
            style={{
              backgroundImage: "linear-gradient(90deg, var(--gis-green-dark), var(--gis-green))",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            Design System · Gruppo Impresa Suite
          </h1>
          <p className="text-slate-600 gis-body mt-1">
            Vetrina live della palette, tipografia, utility classes e pattern del brand. Clicca su
            un colore per copiare la variabile CSS.
          </p>
        </div>
        {onClose && (
          <Button variant="outline" onClick={onClose} data-testid="ds-close">
            <X className="mr-2 h-4 w-4" /> Chiudi
          </Button>
        )}
      </div>

      {/* ── PALETTE ─────────────────────────────────────── */}
      <Section
        id="palette"
        icon={Palette}
        title="Palette colori"
        subtitle="Ogni token è cliccabile e copia var(--gis-*) negli appunti. La palette è centralizzata in src/index.css."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {COLOR_TOKENS.map((t) => (
            <ColorSwatch key={t.name} token={t} />
          ))}
        </div>
      </Section>

      {/* ── TIPOGRAFIA ─────────────────────────────────── */}
      <Section
        id="typography"
        icon={TypeIcon}
        title="Tipografia"
        subtitle="Montserrat per titoli e bottoni · Inter per corpo, form, metadati. Caricati via Google Fonts."
      >
        <div className="border border-slate-200 rounded-lg divide-y">
          {FONT_SPECS.map((f) => (
            <div key={f.name} className="grid grid-cols-1 sm:grid-cols-[220px_1fr_120px] items-center gap-3 px-4 py-3">
              <div className="text-xs text-slate-500 uppercase tracking-wider gis-body">{f.name}</div>
              <div
                className={`gis-heading ${f.size}`}
                style={{ fontFamily: f.family === "Inter" ? "var(--gis-font-body)" : "var(--gis-font-heading)", fontWeight: f.weight }}
              >
                Gestionale di Studio
              </div>
              <div className="text-[10px] font-mono text-slate-400 text-right">
                {f.family} {f.weight}<br/>{f.size}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── UTILITY CLASSES ─────────────────────────────── */}
      <Section
        id="utilities"
        icon={Layers}
        title="Utility classes"
        subtitle="Classi CSS riusabili opt-in per applicare il brand progressivamente ai componenti."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {UTILITY_CLASSES.map((u) => (
            <div key={u.cls} className="flex items-center gap-3 border border-slate-200 rounded px-3 py-2 bg-white">
              <code className="gis-body text-xs font-semibold text-[var(--gis-green)] shrink-0">{u.cls}</code>
              <span className="text-xs text-slate-600 gis-body">{u.desc}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ── ICONOGRAFIA STANDARD ────────────────────────── */}
      <Section
        id="iconography"
        icon={Sparkles}
        title="Iconografia standard"
        subtitle="Un solo glifo per significato. Usa lucide-react. Clicca su un'icona per copiarne il nome (import)."
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="ds-icon-library">
          {ICON_LIBRARY.map((it) => (
            <IconTile key={it.name} entry={it} />
          ))}
        </div>
        <div className="mt-3 text-xs text-slate-500 gis-body flex flex-wrap gap-x-6 gap-y-1">
          <span><b>Dimensioni</b>: h-4 w-4 bottoni · h-3.5 w-3.5 tabelle · h-5 w-5 alert · h-6 w-6 empty-state</span>
          <span><b>Colori</b>: verde brand (azioni) · rosso (delete) · oro (warning) · slate (neutro)</span>
        </div>
      </Section>

      {/* ── BOTTONI ─────────────────────────────────────── */}
      <Section
        id="buttons"
        icon={MousePointerClick}
        title="Bottoni"
        subtitle="Uso: primary per l'azione principale · outline verde per azioni secondarie · destructive/ghost per azioni funzionali."
      >
        <div className="flex flex-wrap gap-3 items-center bg-white border border-slate-200 rounded-lg p-4">
          <Button className="gis-btn-primary" data-testid="ds-btn-primary">
            <Sparkles className="mr-2 h-4 w-4" /> Salva Cliente
          </Button>
          <Button
            variant="outline"
            className="gis-border-green gis-text-green hover:bg-[var(--gis-green-50)]"
            data-testid="ds-btn-outline"
          >
            Nuovo Preventivo
          </Button>
          <Button variant="secondary">Annulla</Button>
          <Button variant="destructive">Elimina</Button>
          <Button variant="ghost" size="icon">
            <Bell className="h-4 w-4" />
          </Button>
          <Button
            className="text-white"
            style={{ backgroundColor: "var(--gis-gold-dark)" }}
            data-testid="ds-btn-gold"
          >
            Highlight Oro
          </Button>
        </div>
      </Section>

      {/* ── TABS ────────────────────────────────────────── */}
      <Section
        id="tabs"
        icon={Layers}
        title="Tabs"
        subtitle="TabsTrigger con classe gis-heading + verde brand come active state."
      >
        <Tabs value={demoTab} onValueChange={setDemoTab} className="w-full">
          <TabsList className="grid grid-cols-3 gap-1 h-auto p-1 bg-[var(--gis-green-50)] rounded-lg max-w-md">
            {["uno", "due", "tre"].map((v) => (
              <TabsTrigger
                key={v}
                value={v}
                className="gis-heading flex-1 justify-center data-[state=active]:bg-[var(--gis-green)] data-[state=active]:text-white hover:bg-white hover:text-[var(--gis-green)] transition-colors"
              >
                Tab {v}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="uno" className="mt-4 text-slate-600 gis-body">
            Contenuto tab «uno» — active state in verde brand.
          </TabsContent>
          <TabsContent value="due" className="mt-4 text-slate-600 gis-body">
            Contenuto tab «due» — hover in mint tenue.
          </TabsContent>
          <TabsContent value="tre" className="mt-4 text-slate-600 gis-body">
            Contenuto tab «tre».
          </TabsContent>
        </Tabs>
      </Section>

      {/* ── FORM ────────────────────────────────────────── */}
      <Section
        id="form"
        icon={TypeIcon}
        title="Campi form"
        subtitle="Input/Textarea con focus ring verde brand tramite .gis-focus-ring o :focus-visible custom."
      >
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3 max-w-lg">
          <div className="space-y-1">
            <Label htmlFor="ds-input" className="gis-body font-medium text-slate-700 text-sm">
              Nome cliente
            </Label>
            <Input
              id="ds-input"
              placeholder="Inserisci il nome…"
              value={demoInput}
              onChange={(e) => setDemoInput(e.target.value)}
              className="gis-focus-ring focus:border-[var(--gis-green)]"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ds-textarea" className="gis-body font-medium text-slate-700 text-sm">
              Note interne
            </Label>
            <Textarea
              id="ds-textarea"
              placeholder="Scrivi una nota…"
              rows={3}
              className="gis-focus-ring focus:border-[var(--gis-green)]"
            />
          </div>
        </div>
      </Section>

      {/* ── DIALOG ──────────────────────────────────────── */}
      <Section
        id="dialog"
        icon={Sparkles}
        title="Dialog"
        subtitle="Header con barra brand verde in cima + titolo Montserrat verde."
      >
        <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gis-btn-primary" data-testid="ds-open-dialog">
              Apri Dialog demo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md p-0 gap-0 overflow-hidden">
            <div className="h-1.5 w-full" style={{ backgroundColor: "var(--gis-green)" }} />
            <div className="px-6 pt-4 pb-6 space-y-4">
              <DialogHeader>
                <DialogTitle
                  className="gis-heading"
                  style={{ color: "var(--gis-green)" }}
                >
                  Titolo del Dialog
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-600 gis-body">
                Corpo del dialog. Barra brand verde in cima e titolo Montserrat in
                <span className="font-semibold" style={{ color: "var(--gis-green)" }}> verde brand</span>.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDemoDialogOpen(false)}>
                  Annulla
                </Button>
                <Button className="gis-btn-primary" onClick={() => setDemoDialogOpen(false)}>
                  Conferma
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </Section>

      {/* ── BANNERS / ALERT ──────────────────────────────── */}
      <Section
        id="alerts"
        icon={AlertTriangle}
        title="Banner / Alert"
        subtitle="Palette semantica: success verde brand · warning oro brand · errore rosso · info neutro."
      >
        <div className="space-y-3">
          <div
            className="p-3 rounded-lg flex items-center gap-3"
            style={{ backgroundColor: "var(--gis-green-50)", border: "1px solid var(--gis-green-200)" }}
          >
            <CheckCircle2 className="h-5 w-5" style={{ color: "var(--gis-green)" }} />
            <div className="gis-body text-sm" style={{ color: "var(--gis-green-dark)" }}>
              <b>Operazione completata</b> — Il cliente è stato salvato correttamente.
            </div>
          </div>
          <div
            className="p-3 rounded-lg flex items-center gap-3"
            style={{ backgroundColor: "var(--gis-gold-50)", border: "1px solid var(--gis-gold)" }}
          >
            <AlertTriangle className="h-5 w-5" style={{ color: "var(--gis-gold-dark)" }} />
            <div className="gis-body text-sm" style={{ color: "var(--gis-gold-dark)" }}>
              <b>Attenzione</b> — Identità operatore non ancora configurata.
            </div>
          </div>
          <div className="p-3 rounded-lg flex items-center gap-3 bg-red-50 border border-red-200">
            <XCircle className="h-5 w-5 text-red-600" />
            <div className="gis-body text-sm text-red-800">
              <b>Errore</b> — Impossibile connettersi al server.
            </div>
          </div>
          <div className="p-3 rounded-lg flex items-center gap-3 bg-blue-50 border border-blue-200">
            <Info className="h-5 w-5 text-blue-600" />
            <div className="gis-body text-sm text-blue-800">
              <b>Info</b> — Sono disponibili aggiornamenti al gestionale.
            </div>
          </div>
        </div>
      </Section>

      {/* ── TOAST ───────────────────────────────────────── */}
      <Section
        id="toast"
        icon={Bell}
        title="Toast (Sonner)"
        subtitle="Toast personalizzati con palette brand: success/error/warning."
      >
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="gis-border-green gis-text-green"
            onClick={() => toast.success("Salvataggio completato")}
            data-testid="ds-toast-success"
          >
            Trigger toast success
          </Button>
          <Button
            variant="outline"
            style={{ borderColor: "var(--gis-gold-dark)", color: "var(--gis-gold-dark)" }}
            onClick={() => toast.warning("Attenzione: verifica i dati")}
            data-testid="ds-toast-warning"
          >
            Trigger toast warning
          </Button>
          <Button
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50"
            onClick={() => toast.error("Errore di connessione")}
            data-testid="ds-toast-error"
          >
            Trigger toast error
          </Button>
          <Button
            variant="outline"
            onClick={() => toast("Info generica")}
            data-testid="ds-toast-info"
          >
            Trigger toast info
          </Button>
        </div>
      </Section>

      {/* ── BADGES ──────────────────────────────────────── */}
      <Section
        id="badges"
        icon={Sparkles}
        title="Badge"
        subtitle="Etichette compatte per stato o tag."
      >
        <div className="flex flex-wrap gap-2">
          <Badge style={{ backgroundColor: "var(--gis-green)", color: "white" }}>Attivo</Badge>
          <Badge style={{ backgroundColor: "var(--gis-green-100)", color: "var(--gis-green-dark)" }}>In corso</Badge>
          <Badge style={{ backgroundColor: "var(--gis-gold-50)", color: "var(--gis-gold-dark)", border: "1px solid var(--gis-gold)" }}>Bozza</Badge>
          <Badge variant="destructive">Scaduto</Badge>
          <Badge variant="secondary">Neutro</Badge>
          <Badge variant="outline">Outline</Badge>
        </div>
      </Section>

      {/* ── DO / DON'T ─────────────────────────────────── */}
      <Section
        id="do-dont"
        icon={CheckCircle2}
        title="Do & Don't"
        subtitle="Sintesi delle regole d'oro. La versione completa è in /app/memory/design_guidelines.md."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card style={{ borderColor: "var(--gis-green-200)" }}>
            <CardHeader className="pb-2">
              <CardTitle className="gis-heading text-base flex items-center gap-2" style={{ color: "var(--gis-green)" }}>
                <CheckCircle2 className="h-4 w-4" /> DO
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm gis-body space-y-2 text-slate-700">
              <p>• Usa sempre <code>var(--gis-*)</code> invece di HEX inline</p>
              <p>• Preferisci utility classes <code>gis-*</code> alle proprietà CSS ripetute</p>
              <p>• Mantieni la semantica funzionale (rosso=errore, arancione=warning)</p>
              <p>• Aggiungi <code>data-testid</code> agli elementi interattivi</p>
              <p>• Montserrat per titoli/bottoni, Inter per corpo/form</p>
            </CardContent>
          </Card>
          <Card className="border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="gis-heading text-base flex items-center gap-2 text-red-700">
                <XCircle className="h-4 w-4" /> DON&apos;T
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm gis-body space-y-2 text-slate-700">
              <p>• Non hardcodare HEX brand (<code>#08684F</code>) nei componenti</p>
              <p>• Non cambiare la semantica (scadenze rosse in verde brand)</p>
              <p>• Non aggiungere colori nuovi senza documentarli in <code>index.css</code> e <code>design_guidelines.md</code></p>
              <p>• Non mescolare font family tra componenti simili</p>
              <p>• Non usare <code>focus:ring-amber-*</code> — sostituisci con <code>.gis-focus-ring</code></p>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* ── COMPONENTI PATTERN ─────────────────────────── */}
      <Section
        id="patterns"
        icon={LayoutGrid}
        title="Componenti pattern"
        subtitle="Blueprint di pagine complete pronti da copiare quando aggiungi nuovi moduli alla Suite (Portfolio, Gestionale Fatture, ecc.)."
      >
        <Tabs defaultValue="kpi-grid" className="w-full">
          <TabsList className="flex flex-wrap gap-1 h-auto p-1 bg-[var(--gis-green-50)] rounded-lg mb-4">
            {[
              { v: "kpi-grid", l: "Card KPI Grid" },
              { v: "module-layout", l: "Module Layout (v1.7)" },
              { v: "form-2col", l: "Form 2 colonne" },
              { v: "table-toolbar", l: "Table + Toolbar" },
              { v: "empty-state", l: "Empty state" },
              { v: "wizard", l: "Wizard multi-step" },
              { v: "print-a4", l: "Print A4 / Landscape" },
            ].map((t) => (
              <TabsTrigger
                key={t.v}
                value={t.v}
                className="gis-heading data-[state=active]:bg-[var(--gis-green)] data-[state=active]:text-white hover:bg-white hover:text-[var(--gis-green)] transition-colors"
              >
                {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Pattern 1 — KPI Grid */}
          <TabsContent value="kpi-grid" className="mt-2 space-y-3">
            <div className="flex justify-end">
              <CopySnippetButton snippet={SNIPPETS.kpiGrid} testId="ds-copy-kpi-grid" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Clienti totali", value: "127", sub: "+3 questa settimana", icon: UsersIcon, tone: "green" },
                { label: "Pratiche attive", value: "41", sub: "8 in scadenza", icon: FileText, tone: "green" },
                { label: "Fatture da incassare", value: "€ 12.480", sub: "3 documenti", icon: Euro, tone: "gold" },
                { label: "Scadute", value: "2", sub: "richiedono azione", icon: Clock, tone: "red" },
              ].map((k) => (
                <div
                  key={k.label}
                  className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow"
                  style={{ borderColor: "var(--gis-green-200)" }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="gis-heading text-[10px] uppercase tracking-wider text-slate-500">
                      {k.label}
                    </p>
                    <k.icon
                      className="h-4 w-4"
                      style={{
                        color:
                          k.tone === "gold" ? "var(--gis-gold-dark)" :
                          k.tone === "red"  ? "var(--gis-red)" :
                          "var(--gis-green)",
                      }}
                    />
                  </div>
                  <p className="gis-heading text-2xl font-bold text-slate-900 tabular-nums">
                    {k.value}
                  </p>
                  <p className="text-xs text-slate-500 gis-body">{k.sub}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Pattern 1.5 — Module Layout (UX Operativa v2, v1.7.0) */}
          <TabsContent value="module-layout" className="mt-2 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="text-xs text-slate-600 gis-body max-w-2xl">
                <b>Standard operativo</b> per ogni modulo della Suite. 5 zone verticali
                (Header 80px · KPI-strip 60px · Toolbar 56px · Body · Footer). La tabella
                riceve ≥80% dell&apos;altezza schermo. Su Full HD (1920×1080) restano visibili
                15-20 righe senza scroll. Le altezze sono governate dalle CSS variables
                <code>--gis-mod-header-h/-kpi-h/-toolbar-h</code>.
              </div>
              <CopySnippetButton snippet={SNIPPETS.moduleLayout} testId="ds-copy-module-layout" />
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
              <div className="grid grid-rows-[80px_60px_56px_240px] divide-y divide-slate-200 bg-white">
                <div className="flex items-center justify-between px-4">
                  <div className="flex items-center gap-3">
                    <span className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--gis-green-50)" }}>
                      <LayoutGrid className="h-5 w-5" style={{ color: "var(--gis-green)" }} />
                    </span>
                    <div>
                      <h4 className="gis-heading font-bold text-slate-900" style={{ fontSize: 20 }}>Titolo Modulo</h4>
                      <div className="gis-body text-xs text-slate-500">Header · max 80px</div>
                    </div>
                  </div>
                  <Button className="gis-btn-primary h-9" data-testid="ds-mod-primary"><Plus className="h-4 w-4 mr-1.5"/>Nuovo</Button>
                </div>
                <div className="flex items-center gap-2 px-4">
                  <KpiPill value={124} label="Totali" tone="brand" active />
                  <KpiPill value={12}  label="Aperti" tone="warning" />
                  <KpiPill value={98}  label="Chiusi" tone="success" />
                  <KpiPill value={8}   label="Da Fatt." tone="danger" />
                  <KpiPill value={3}   label="Oggi" tone="info" />
                  <span className="ml-auto text-[10px] text-slate-400 gis-heading-wide">KPI · max 60px</span>
                </div>
                <div className="flex items-center gap-2 px-4">
                  <div className="relative flex-1 max-w-[300px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input placeholder="Cerca…" className="pl-8 h-9 text-sm" data-testid="ds-mod-search"/>
                  </div>
                  <div className="h-9 w-[120px] border rounded flex items-center justify-center text-xs text-slate-500">Cliente ▼</div>
                  <div className="h-9 w-[120px] border rounded flex items-center justify-center text-xs text-slate-500">Stato ▼</div>
                  <div className="h-9 w-[120px] border rounded flex items-center justify-center text-xs text-slate-500">Periodo ▼</div>
                  <span className="ml-auto text-[10px] text-slate-400 gis-heading-wide">Toolbar · max 56px</span>
                </div>
                <div className="p-4 flex items-center justify-center bg-[repeating-linear-gradient(45deg,var(--gis-green-50),var(--gis-green-50)_10px,white_10px,white_20px)]">
                  <span className="text-slate-600 gis-heading text-sm bg-white/80 px-3 py-1 rounded">Area operativa · Tabella (≥80% altezza schermo)</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 gis-body">
              Applicato in <b>v1.7.0</b> al modulo <code>Registro Pratiche</code>
              (<code>PraticheTab.jsx</code>). Rollout progressivo su Fatture, Clienti,
              Modulistica, Attività e Preventivi nelle release successive.
            </p>
          </TabsContent>

          {/* Pattern 2 — Form 2 colonne */}
          <TabsContent value="form-2col" className="mt-2 space-y-3">
            <div className="flex justify-end">
              <CopySnippetButton snippet={SNIPPETS.form2col} testId="ds-copy-form-2col" />
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="h-1.5 w-full" style={{ backgroundColor: "var(--gis-green)" }} />
              <div className="p-5 space-y-4">
                <h3 className="gis-heading text-lg font-semibold" style={{ color: "var(--gis-green)" }}>
                  Nuovo Cliente
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="gis-body text-sm text-slate-700 font-medium">Cognome</Label>
                    <Input placeholder="es. Rossi" className="focus:border-[var(--gis-green)]" />
                  </div>
                  <div className="space-y-1">
                    <Label className="gis-body text-sm text-slate-700 font-medium">Nome</Label>
                    <Input placeholder="es. Mario" className="focus:border-[var(--gis-green)]" />
                  </div>
                  <div className="space-y-1">
                    <Label className="gis-body text-sm text-slate-700 font-medium">Codice Fiscale</Label>
                    <Input placeholder="RSSMRA80A01H501U" className="focus:border-[var(--gis-green)]" />
                  </div>
                  <div className="space-y-1">
                    <Label className="gis-body text-sm text-slate-700 font-medium">Email</Label>
                    <Input placeholder="mario.rossi@example.it" className="focus:border-[var(--gis-green)]" />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label className="gis-body text-sm text-slate-700 font-medium">Note interne</Label>
                    <Textarea
                      rows={3}
                      placeholder="Contesto, preferenze, promemoria…"
                      className="focus:border-[var(--gis-green)]"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                  <Button variant="outline">Annulla</Button>
                  <Button className="gis-btn-primary">Salva Cliente</Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Pattern 3 — Table con Toolbar */}
          <TabsContent value="table-toolbar" className="mt-2 space-y-3">
            <div className="flex justify-end">
              <CopySnippetButton snippet={SNIPPETS.tableToolbar} testId="ds-copy-table-toolbar" />
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {/* Toolbar */}
              <div className="px-4 py-3 border-b border-slate-100 bg-[var(--gis-green-50)]/60 flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                  <Input placeholder="Cerca…" className="pl-8 h-9 focus:border-[var(--gis-green)]" />
                </div>
                <Button variant="outline" size="sm" className="gis-border-green gis-text-green">
                  <Filter className="mr-1.5 h-3.5 w-3.5" /> Filtri
                </Button>
                <div className="flex-1" />
                <Button size="sm" className="gis-btn-primary">
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Nuovo
                </Button>
              </div>
              {/* Table */}
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2 gis-heading text-[11px] uppercase tracking-wider text-slate-600">
                      Numero
                    </th>
                    <th className="text-left px-4 py-2 gis-heading text-[11px] uppercase tracking-wider text-slate-600">
                      Cliente
                    </th>
                    <th className="text-left px-4 py-2 gis-heading text-[11px] uppercase tracking-wider text-slate-600">
                      Importo
                    </th>
                    <th className="text-left px-4 py-2 gis-heading text-[11px] uppercase tracking-wider text-slate-600">
                      Stato
                    </th>
                    <th className="text-right px-4 py-2 gis-heading text-[11px] uppercase tracking-wider text-slate-600">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    { n: "F-0142", c: "Rossi Mario", i: "€ 1.220,00", s: "Pagata", tone: "green" },
                    { n: "F-0141", c: "Bianchi SRL",  i: "€ 3.480,00", s: "Emessa", tone: "gold" },
                    { n: "F-0140", c: "Verdi Anna",   i: "€ 850,00",   s: "Scaduta", tone: "red" },
                  ].map((r) => (
                    <tr key={r.n} className="hover:bg-[var(--gis-green-50)]/40">
                      <td className="px-4 py-2.5 gis-body font-medium text-slate-800">{r.n}</td>
                      <td className="px-4 py-2.5 gis-body text-slate-700">{r.c}</td>
                      <td className="px-4 py-2.5 gis-body tabular-nums text-slate-700">{r.i}</td>
                      <td className="px-4 py-2.5">
                        <Badge
                          style={{
                            backgroundColor:
                              r.tone === "green" ? "var(--gis-green)" :
                              r.tone === "gold"  ? "var(--gis-gold-50)" :
                              "var(--gis-red)",
                            color:
                              r.tone === "gold" ? "var(--gis-gold-dark)" : "white",
                            border: r.tone === "gold" ? "1px solid var(--gis-gold)" : "none",
                          }}
                        >
                          {r.s}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Button variant="ghost" size="sm">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between gis-body text-xs text-slate-500">
                <span>3 di 3 elementi</span>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" disabled>‹ Precedente</Button>
                  <Button variant="ghost" size="sm" disabled>Successiva ›</Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Pattern 4 — Empty state */}
          <TabsContent value="empty-state" className="mt-2 space-y-3">
            <div className="flex justify-end">
              <CopySnippetButton snippet={SNIPPETS.emptyState} testId="ds-copy-empty-state" />
            </div>
            <div className="bg-white border border-dashed border-slate-300 rounded-lg py-12 px-6 flex flex-col items-center text-center">
              {/* Pallini brand (rif. login) */}
              <div className="flex items-center gap-2 mb-4" aria-hidden="true">
                <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: "var(--gis-red)" }} />
                <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: "var(--gis-gray)" }} />
                <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: "var(--gis-gold)" }} />
              </div>
              <h3 className="gis-heading text-lg font-semibold" style={{ color: "var(--gis-green)" }}>
                Nessun elemento
              </h3>
              <p className="text-sm text-slate-500 gis-body mt-1 max-w-md">
                Non ci sono ancora dati da mostrare. Inizia creando il primo elemento — apparirà in automatico qui.
              </p>
              <div className="mt-4 flex gap-2">
                <Button className="gis-btn-primary">
                  <Plus className="mr-1.5 h-4 w-4" /> Crea il primo
                </Button>
                <Button variant="outline" className="gis-border-green gis-text-green">
                  Importa da CSV
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Pattern 5 — Wizard multi-step */}
          <TabsContent value="wizard" className="mt-2 space-y-3">
            <div className="flex justify-end">
              <CopySnippetButton snippet={SNIPPETS.wizard} testId="ds-copy-wizard" />
            </div>
            <WizardDemo />
            <p className="text-xs text-slate-500 gis-body">
              Uso tipico: onboarding cliente, apertura pratica complessa, procedure amministrative
              multi-fase. Lo stepper resta sempre visibile in cima e i bottoni Avanti / Indietro
              in fondo al contenitore. Ogni step controlla la propria validazione prima di
              consentire l&apos;avanzamento.
            </p>
          </TabsContent>

          {/* Pattern 6 — Print A4 / Landscape */}
          <TabsContent value="print-a4" className="mt-2 space-y-3">
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gis-border-green gis-text-green hover:bg-[var(--gis-green-50)]"
                onClick={() => {
                  const bodyHtml = `
                    <h2>Riepilogo attività</h2>
                    <table>
                      <thead><tr><th>Data</th><th>Pratica</th><th>Cliente</th><th>Azione</th><th>Operatore</th></tr></thead>
                      <tbody>
                        <tr><td>21/07/2026 09:14</td><td>GS-2466</td><td>Rossi Mario</td><td>Pratica creata</td><td>Paola Bonaiuti</td></tr>
                        <tr><td>21/07/2026 10:02</td><td>GS-2466</td><td>Rossi Mario</td><td>Documento caricato</td><td>Paola Bonaiuti</td></tr>
                        <tr><td>21/07/2026 11:45</td><td>GS-2467</td><td>Bianchi SRL</td><td>Fattura n. F-0142 emessa (€ 1.220,00)</td><td>Valentina Marzi</td></tr>
                        <tr><td>21/07/2026 15:30</td><td>GS-2465</td><td>Verdi Anna</td><td>Sollecito pagamento inviato</td><td>Paola Bonaiuti</td></tr>
                      </tbody>
                    </table>
                    <h2>Note</h2>
                    <p>Documento generato dal blueprint di stampa unificato del Design System Gruppo Impresa Suite. Il footer istituzionale mostra <strong>data stampa · Suite · Banca dati · Revisione</strong> su ogni pagina.</p>`;
                  const html = buildPrintDocument({
                    title: "Demo stampa unificata",
                    subtitle: "Layout A4 landscape · v1.5.0 · Gruppo Impresa Suite",
                    bodyHtml,
                    orientation: "landscape",
                    studioName: "Studio Bonaiuti STP",
                    studioLine: "Via Roma 1 · 00058 Santa Marinella (RM) · info@gruppoimpresa.org",
                  });
                  if (!openPrintWindow(html)) toast.error("Popup bloccato dal browser — abilita i popup.");
                }}
                data-testid="ds-print-landscape"
              >
                <Printer className="mr-1.5 h-4 w-4" /> Stampa demo Landscape
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gis-border-green gis-text-green hover:bg-[var(--gis-green-50)]"
                onClick={() => {
                  const bodyHtml = `
                    <h2>Sezione documento</h2>
                    <p>Questo è un esempio di stampa <strong>A4 verticale (portrait)</strong> con lo stesso layout unificato.</p>
                    <p>Il footer istituzionale in basso mostra automaticamente su ogni pagina la data di stampa, il nome della Suite, la banca dati e la revisione dell'applicativo.</p>`;
                  const html = buildPrintDocument({
                    title: "Demo stampa unificata",
                    subtitle: "Layout A4 portrait",
                    bodyHtml,
                    orientation: "portrait",
                    studioName: "Studio Bonaiuti STP",
                  });
                  if (!openPrintWindow(html)) toast.error("Popup bloccato dal browser.");
                }}
                data-testid="ds-print-portrait"
              >
                <Printer className="mr-1.5 h-4 w-4" /> Stampa demo Portrait
              </Button>
              <CopySnippetButton snippet={SNIPPETS.printA4} testId="ds-copy-print-a4" />
            </div>

            {/* Anteprima ridotta del layout stampa */}
            <div className="bg-slate-100 rounded-lg p-6 flex justify-center">
              <div
                className="bg-white shadow-xl w-full max-w-[560px] overflow-hidden"
                style={{ aspectRatio: "297/210" /* A4 landscape */ }}
              >
                {/* Header */}
                <div className="px-5 pt-3 pb-2 flex items-start justify-between border-b-2" style={{ borderColor: "var(--gis-green)" }}>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--gis-red)" }} />
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--gis-gray)" }} />
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--gis-gold)" }} />
                    </div>
                    <span className="gis-heading text-[9px] font-bold tracking-widest">GRUPPO IMPRESA SUITE</span>
                  </div>
                  <div className="text-right">
                    <div className="gis-heading text-[9px] font-semibold" style={{ color: "var(--gis-green)" }}>Studio Bonaiuti STP</div>
                    <div className="text-[7px] text-slate-500">Via Roma 1 · Santa Marinella (RM)</div>
                  </div>
                </div>
                {/* Title */}
                <div className="px-5 pt-2">
                  <h4 className="gis-heading text-sm font-bold" style={{ color: "var(--gis-green-dark)" }}>
                    Titolo del documento
                  </h4>
                  <p className="text-[8px] text-slate-500">Sottotitolo o metadati</p>
                  <div className="h-1 mt-1 rounded" style={{ background: "linear-gradient(90deg, var(--gis-green) 0 40%, var(--gis-gold) 40% 55%, var(--gis-green) 55% 100%)" }} />
                </div>
                {/* Body */}
                <div className="px-5 py-3 space-y-1.5">
                  <div className="h-3 rounded bg-slate-100" />
                  <div className="h-3 rounded bg-slate-100 w-4/5" />
                  <div className="h-6 rounded bg-[var(--gis-green-50)]" />
                  <div className="h-3 rounded bg-slate-100" />
                  <div className="h-3 rounded bg-slate-100 w-3/4" />
                </div>
                {/* Footer */}
                <PrintFooter
                  className="px-5 py-2 mt-auto"
                  printedAt="21/07/2026 14:22"
                  pageLabel="Pagina 1 / 1"
                  compact
                  data-testid="ds-print-footer-preview"
                />
              </div>
            </div>

            <p className="text-xs text-slate-500 gis-body">
              Il footer istituzionale sopra viene generato automaticamente da{" "}
              <code>buildPrintDocument()</code> su ogni pagina della stampa (frontend) e dal
              componente <code>&lt;PrintFooter /&gt;</code> nelle anteprime in-app. Contiene:
              data/ora stampa · nome Suite · banca dati usata · versione + data ·
              paginazione X / Y.
            </p>
          </TabsContent>
        </Tabs>
      </Section>

      {/* Footer info */}
      <div className="pt-6 border-t border-slate-200 text-xs text-slate-500 gis-body text-center">
        Design System v{APP_RELEASE.version} · {APP_RELEASE.updatedAt} · Fonte di verità:{" "}
        <code>/app/memory/design_guidelines.md</code>
      </div>
    </div>
  );
}

